import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  healthService, 
  HealthProgramGroupDto, 
  HealthProgramGroupUserDto, 
  HealthProgramGroupCalendarDto,
  setCurrentUser 
} from '../../services/health';
import { useAuth } from 'react-oidc-context';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import PageHeader from '../../components/UI/PageHeader';
import ConfirmDialog from '../../components/UI/ConfirmDialog';
import Tabs from '../../components/UI/Tabs';
import StatusBadge from '../../components/UI/StatusBadge';
import FormInput from '../../components/Forms/FormInput';
import Pagination from '../../components/UI/Pagination';
import Alert from '../../components/UI/Alert';
import { formatDateTime } from '../../utils/formatters';

const ProgramGroupDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const auth = useAuth();

  // Update the current user for the health service
  useEffect(() => {
    setCurrentUser(auth.user || null);
  }, [auth.user]);
  const [programGroup, setProgramGroup] = useState<HealthProgramGroupDto | null>(null);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>('details');
  
  const [users, setUsers] = useState<HealthProgramGroupUserDto[]>([]);
  const [usersLoading, setUsersLoading] = useState<boolean>(false);
  const [usersTotalElements, setUsersTotalElements] = useState<number>(0);
  const [usersCurrentPage, setUsersCurrentPage] = useState<number>(1);
  const [usersPageSize] = useState<number>(10);
  const [syncStatus, setSyncStatus] = useState<{userId: string, status: 'success' | 'error' | null, message: string}>({
    userId: '',
    status: null,
    message: ''
  });
  
  // Calendar states
  const [calendars, setCalendars] = useState<HealthProgramGroupCalendarDto[]>([]);
  const [calendarsLoading, setCalendarsLoading] = useState<boolean>(false);
  const [calendarsTotalElements, setCalendarsTotalElements] = useState<number>(0);
  const [calendarsCurrentPage, setCalendarsCurrentPage] = useState<number>(1);
  const [calendarsPageSize] = useState<number>(10);

  const [formData, setFormData] = useState<HealthProgramGroupDto>({
    name: '',
    description: ''
  });

  const fetchProgramGroup = useCallback(async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const data = await healthService.getProgramGroupById(id);
      setProgramGroup(data);
      setFormData({
        name: data.name,
        description: data.description
      });
    } catch (error) {
      console.error('Error fetching program group:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchUsers = useCallback(async () => {
    if (!id) return;
    
    setUsersLoading(true);
    try {
      const response = await healthService.getProgramGroupUsers(id, usersCurrentPage, usersPageSize);
      setUsers(response.content || []);
      setUsersTotalElements(response.total_elements || 0);
    } catch (error) {
      console.error('Error fetching program group users:', error);
    } finally {
      setUsersLoading(false);
    }
  }, [id, usersCurrentPage, usersPageSize]);
  
  const fetchCalendars = useCallback(async () => {
    if (!id) return;
    
    setCalendarsLoading(true);
    try {
      const response = await healthService.getProgramGroupCalendars(id, calendarsCurrentPage, calendarsPageSize);
      setCalendars(response.content || []);
      setCalendarsTotalElements(response.total_elements || 0);
    } catch (error) {
      console.error('Error fetching program group calendars:', error);
    } finally {
      setCalendarsLoading(false);
    }
  }, [id, calendarsCurrentPage, calendarsPageSize]);

  useEffect(() => {
    fetchProgramGroup();
  }, [fetchProgramGroup]);

  useEffect(() => {
    if (activeTab === 'users' && id) {
      fetchUsers();
    } else if (activeTab === 'calendars' && id) {
      fetchCalendars();
    }
  }, [id, activeTab, usersCurrentPage, calendarsCurrentPage, fetchUsers, fetchCalendars]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!id) return;
    
    setSaving(true);
    try {
      const updatedGroup = await healthService.updateProgramGroup(id, formData);
      setProgramGroup(updatedGroup);
      setEditMode(false);
    } catch (error) {
      console.error('Error updating program group:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (programGroup) {
      setFormData({
        name: programGroup.name,
        description: programGroup.description
      });
    }
    setEditMode(false);
  };

  const handleUsersPageChange = (page: number) => {
    setUsersCurrentPage(page);
  };
  
  const handleCalendarsPageChange = (page: number) => {
    setCalendarsCurrentPage(page);
  };

  const handleUserDetach = async (userId: string) => {
    if (!id) return;
    
    try {
      await healthService.detachProgramGroupUser(id, userId);
      fetchUsers();
    } catch (error) {
      console.error('Error detaching user:', error);
    }
  };

  const handleUserSync = async (userId: string) => {
    if (!id) return;
    
    setSyncStatus({
      userId,
      status: null,
      message: ''
    });
    
    try {
      await healthService.attachProgramGroupUserSync(id, userId);
      setSyncStatus({
        userId,
        status: 'success',
        message: 'User schedules synced successfully'
      });
      
      // Clear the status message after 5 seconds
      setTimeout(() => {
        setSyncStatus(prev => prev.userId === userId ? { userId: '', status: null, message: '' } : prev);
      }, 5000);
    } catch (error) {
      console.error('Error syncing user schedules:', error);
      setSyncStatus({
        userId,
        status: 'error',
        message: 'Failed to sync user schedules. Please try again.'
      });
    }
  };


  // Content is handled separately in the component based on activeTab
  const tabs = [
    { id: 'details', label: 'Details', content: undefined },
    { id: 'users', label: 'Users', content: undefined },
    { id: 'calendars', label: 'Calendars', content: undefined }
  ];

  return (
    <div>
      <PageHeader
        title={loading ? 'Loading...' : !programGroup ? 'Not Found' : editMode ? 'Edit Program Group' : programGroup.name || 'Program Group Details'}
        breadcrumbs={[
          { label: "Dashboard", to: "/" },
          { label: "Health" },
          { label: "Program Groups", to: "/health/program-groups" },
          { label: loading ? 'Loading...' : !programGroup ? 'Not Found' : programGroup.name || 'Details' }
        ]}
        actions={
          <Button
            variant="secondary"
            onClick={() => navigate('/health/program-groups')}
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Back to Program Groups
          </Button>
        }
      />

      {!loading && programGroup && !editMode && (
        <div className="mb-6">
          <Button variant="primary" onClick={() => setEditMode(true)}>
            Edit Group
          </Button>
        </div>
      )}

      <Card>
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : !programGroup ? (
          <div className="bg-yellow-50 text-yellow-700 p-4 rounded-md mb-4">
            Program group not found
            <Button 
              variant="ghost" 
              size="sm" 
              className="ml-2"
              onClick={() => navigate('/health/program-groups')}
            >
              Back to Program Groups
            </Button>
          </div>
        ) : (
          <div>
            <div className="p-6">
              <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
            </div>

            {activeTab === 'details' && (
              <div>
                {editMode ? (
                  <div className="p-6 space-y-4">
                    <FormInput
                      label="Name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                    <FormInput
                      label="Description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      required
                    />
                    <div className="flex justify-end space-x-2 mt-4">
                      <Button variant="secondary" onClick={handleCancel} disabled={saving}>
                        Cancel
                      </Button>
                      <Button variant="primary" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">Name</h3>
                        <p className="mt-1 text-sm text-gray-900">{programGroup?.name}</p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">Description</h3>
                        <p className="mt-1 text-sm text-gray-900">{programGroup?.description}</p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">Created At</h3>
                        <p className="mt-1 text-sm text-gray-900">
                          {programGroup?.created_at ? formatDateTime(programGroup.created_at) : '-'}
                        </p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">Updated At</h3>
                        <p className="mt-1 text-sm text-gray-900">
                          {programGroup?.updated_at ? formatDateTime(programGroup.updated_at) : '-'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'users' && (
              <div>
                <div className="p-6 border-b border-gray-200 flex justify-end items-center">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => navigate(`/health/program-groups/${id}/users/attach`)}
                  >
                    Attach User
                  </Button>
                </div>
                {usersLoading ? (
                  <div className="flex justify-center items-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="min-w-full">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                              User
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                              Email
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                              Status
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {users.length === 0 ? (
                            <tr>
                              <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500">
                                No users attached to this program group
                              </td>
                            </tr>
                          ) : (
                            users.map((user) => (
                              <React.Fragment key={user.id}>
                                <tr className="hover:bg-gray-50">
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                    {user.first_name && user.last_name
                                      ? `${user.first_name} ${user.last_name}`
                                      : user.username || '-'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {user.email || '-'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <StatusBadge status={user.status || 'UNKNOWN'} />
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <div className="flex justify-end space-x-2">
                                      <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => handleUserSync(user.user_id)}
                                        disabled={syncStatus.userId === user.user_id && syncStatus.status === null}
                                      >
                                        {syncStatus.userId === user.user_id && syncStatus.status === null
                                          ? 'Syncing...'
                                          : 'Sync'}
                                      </Button>
                                      <Button
                                        variant="danger"
                                        size="sm"
                                        onClick={() => handleUserDetach(user.user_id)}
                                      >
                                        Detach
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                                {syncStatus.userId === user.user_id && syncStatus.status && (
                                  <tr>
                                    <td colSpan={4} className="px-4 py-0">
                                      <div className="py-2">
                                        <Alert
                                          variant={syncStatus.status}
                                          dismissible
                                          onDismiss={() => setSyncStatus({ userId: '', status: null, message: '' })}
                                        >
                                          {syncStatus.message}
                                        </Alert>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                    {usersTotalElements > usersPageSize && (
                      <div className="py-3 px-6 border-t border-gray-200">
                        <Pagination
                          currentPage={usersCurrentPage}
                          totalItems={usersTotalElements}
                          pageSize={usersPageSize}
                          onPageChange={handleUsersPageChange}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {activeTab === 'calendars' && (
              <div>
                <div className="p-6 border-b border-gray-200 flex justify-end items-center">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => navigate(`/health/program-groups/${id}/calendars/create`)}
                  >
                    Create Calendar
                  </Button>
                </div>
                {calendarsLoading ? (
                  <div className="flex justify-center items-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                  </div>
                ) : (
                  <>
                    {calendars.length === 0 ? (
                      <div className="p-6 text-center text-gray-500">
                        No calendars found for this program group.
                        <div className="mt-2">
                          <Button
                            variant="primary"
                            onClick={() => navigate(`/health/program-groups/${id}/calendars/create`)}
                          >
                            Create Calendar
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="overflow-x-auto">
                          <table className="min-w-full">
                            <thead>
                              <tr className="border-b border-gray-200">
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                                  Name
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                                  Timezone
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                                  Start Date
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                                  End Date
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider">
                                  Actions
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {calendars.map((calendar) => (
                                <tr key={calendar.id} className="hover:bg-gray-50">
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                    {calendar.name}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {calendar.timezone}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {calendar.starts_in ? formatDateTime(calendar.starts_in) : '-'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {calendar.ends_in ? formatDateTime(calendar.ends_in) : '-'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <div className="flex justify-end space-x-2">
                                      <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => navigate(`/health/program-groups/${id}/calendars/${calendar.id}/edit`)}
                                      >
                                        Edit
                                      </Button>
                                      <Button
                                        variant="danger"
                                        size="sm"
                                        onClick={() => navigate(`/health/program-groups/${id}/calendars`)}
                                      >
                                        Manage
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {calendarsTotalElements > calendarsPageSize && (
                          <div className="py-3 px-6 border-t border-gray-200">
                            <Pagination
                              currentPage={calendarsCurrentPage}
                              totalItems={calendarsTotalElements}
                              pageSize={calendarsPageSize}
                              onPageChange={handleCalendarsPageChange}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </Card>

      <ConfirmDialog
        isOpen={showConfirmDialog}
        title="Delete Program Group"
        message="Are you sure you want to delete this program group? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={() => {
          // Delete functionality would go here
          setShowConfirmDialog(false);
        }}
        onCancel={() => setShowConfirmDialog(false)}
      />
    </div>
  );
};

export default ProgramGroupDetails;